import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Image, 
  Modal, 
  Tag, 
  Select, 
  Button, 
  Space,
  Empty,
  Skeleton,
  message,
  Timeline
} from 'antd';
import { 
  CameraOutlined, 
  EyeOutlined, 
  FilterOutlined,
  CalendarOutlined,
  StarOutlined
} from '@ant-design/icons';
// Customer portal service removed
import { useAuth } from '../../../contexts/AuthContext';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const PhotoGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [selectedServiceType, setSelectedServiceType] = useState('all');
  const [selectedPhotoType, setSelectedPhotoType] = useState('all');
  
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser) {
      fetchPhotos();
    }
  }, [currentUser]);

  useEffect(() => {
    filterPhotos();
  }, [photos, selectedServiceType, selectedPhotoType]);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      // Customer portal service removed - using empty photo gallery
      setPhotos([]);
    } catch (error) {
      console.error('Error fetching photos:', error);
      message.error('Failed to load photo gallery');
    } finally {
      setLoading(false);
    }
  };

  const filterPhotos = () => {
    let filtered = photos;

    if (selectedServiceType !== 'all') {
      filtered = filtered.filter(photo => photo.serviceType === selectedServiceType);
    }

    if (selectedPhotoType !== 'all') {
      filtered = filtered.filter(photo => photo.type === selectedPhotoType);
    }

    setFilteredPhotos(filtered);
  };

  const handlePreview = (photo) => {
    setPreviewImage(photo.url);
    setPreviewVisible(true);
  };

  const getServiceIcon = (serviceType) => {
    const icons = {
      'lawn-care': '🌱',
      'painting': '🎨',
      'cleaning': '🧽',
      'maintenance': '🔧',
      'gutter-cleaning': '🏠',
      'landscaping': '🌿'
    };
    return icons[serviceType] || '🔧';
  };

  const getServiceName = (serviceType) => {
    const names = {
      'lawn-care': 'Lawn Care',
      'painting': 'Painting',
      'cleaning': 'Cleaning',
      'maintenance': 'Maintenance',
      'gutter-cleaning': 'Gutter Cleaning',
      'landscaping': 'Landscaping'
    };
    return names[serviceType] || serviceType;
  };

  const getPhotoTypeColor = (type) => {
    const colors = {
      'before': 'orange',
      'after': 'green',
      'general': 'blue'
    };
    return colors[type] || 'default';
  };

  const getPhotoTypeText = (type) => {
    const texts = {
      'before': 'Before',
      'after': 'After',
      'general': 'General'
    };
    return texts[type] || type;
  };

  const uniqueServiceTypes = [...new Set(photos.map(photo => photo.serviceType))];

  if (loading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  if (photos.length === 0) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No photos yet"
        >
          <Text type="secondary">
            Photos from your completed services will appear here
          </Text>
        </Empty>
      </Card>
    );
  }

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              <CameraOutlined style={{ marginRight: '0.5rem' }} />
              Photo Gallery
            </Title>
            <Text type="secondary">
              Before and after photos from your services
            </Text>
          </div>
          <div>
            <Text strong>{photos.length}</Text> photos
          </div>
        </div>

        {/* Filters */}
        <div style={{ marginBottom: '1.5rem' }}>
          <Space wrap>
            <div>
              <Text strong style={{ marginRight: '0.5rem' }}>Service:</Text>
              <Select
                value={selectedServiceType}
                onChange={setSelectedServiceType}
                style={{ width: 150 }}
                size="small"
              >
                <Option value="all">All Services</Option>
                {uniqueServiceTypes.map(serviceType => (
                  <Option key={serviceType} value={serviceType}>
                    {getServiceIcon(serviceType)} {getServiceName(serviceType)}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <Text strong style={{ marginRight: '0.5rem' }}>Type:</Text>
              <Select
                value={selectedPhotoType}
                onChange={setSelectedPhotoType}
                style={{ width: 120 }}
                size="small"
              >
                <Option value="all">All Types</Option>
                <Option value="before">Before</Option>
                <Option value="after">After</Option>
                <Option value="general">General</Option>
              </Select>
            </div>
          </Space>
        </div>

        {/* Photo Grid */}
        <Row gutter={[16, 16]}>
          {filteredPhotos.map((photo, index) => (
            <Col xs={24} sm={12} md={8} lg={6} key={index}>
              <Card
                hoverable
                cover={
                  <div style={{ position: 'relative' }}>
                    <Image
                      src={photo.url}
                      alt={`Service photo ${index + 1}`}
                      style={{ 
                        height: '200px', 
                        objectFit: 'cover',
                        cursor: 'pointer'
                      }}
                      preview={false}
                      onClick={() => handlePreview(photo)}
                    />
                    <Tag
                      color={getPhotoTypeColor(photo.type)}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        margin: 0
                      }}
                    >
                      {getPhotoTypeText(photo.type)}
                    </Tag>
                  </div>
                }
                size="small"
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1rem' }}>{getServiceIcon(photo.serviceType)}</span>
                    <Text strong style={{ fontSize: '0.9rem' }}>
                      {getServiceName(photo.serviceType)}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <CalendarOutlined style={{ fontSize: '0.8rem', color: '#666' }} />
                    <Text type="secondary" style={{ fontSize: '0.8rem' }}>
                      {dayjs(photo.date).format('MMM DD, YYYY')}
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {filteredPhotos.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No photos match your filters"
            />
          </div>
        )}
      </Card>

      {/* Photo Preview Modal */}
      <Modal
        open={previewVisible}
        title="Photo Preview"
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={800}
        centered
      >
        <div style={{ textAlign: 'center' }}>
          <Image
            src={previewImage}
            alt="Preview"
            style={{ maxWidth: '100%', maxHeight: '70vh' }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default PhotoGallery;
